// The A/B comparator (docs/plan/phase-10-ui.md, "Compare"): two encodes of
// the same shot, on the same frame, at the same size -- one of them clipped
// away by a draggable divider, or both side by side. The Library opens it
// with the two items already in hand (navigation state); a link opened cold
// carries only their ids, so whatever is missing is fetched, and the panels
// then show only the facts that survive without the list row's label, attrs
// and probe result.
//
// The left side is always A and the right is B, in both modes, and A drives
// the clock: its `timeupdate` is what re-seats B when the two decoders drift
// apart (lib/compare.ts's driftCorrection). Nothing here transcodes or
// re-encodes anything -- the two files are played exactly as Store serves
// them, which is the whole point: what is being compared is what was
// delivered.
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { getArtifact, type ArtifactListItem } from "../api/client";
import { storeSrc } from "../api/store";
import {
  MAX_DIVIDER_PCT,
  MIN_DIVIDER_PCT,
  compareHref,
  comparePairFromSearchParams,
  compareSearchParams,
  compareSideLine,
  compareSideRows,
  compareSideTitle,
  clampDivider,
  driftCorrection,
  DEFAULT_DIVIDER_PCT,
  steppedTime,
} from "../lib/compare";
import { formatDuration } from "../lib/library";

type CompareMode = "split" | "side";
type Side = "a" | "b";

export default function Compare() {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const pair = useMemo(() => comparePairFromSearchParams(params), [params]);
  const passed = (location.state ?? {}) as { a?: ArtifactListItem; b?: ArtifactListItem };

  // The two artifacts by id. A click in the Library hands both over whole;
  // a reload or a shared link has only the ids, and ArtifactView carries the
  // download URLs but not the label, attrs or probe result -- so a side
  // fetched this way shows what it can and no more.
  const [items, setItems] = useState<Record<string, ArtifactListItem>>(() => {
    const seed: Record<string, ArtifactListItem> = {};
    if (pair && passed.a?.id === pair.a) seed[pair.a] = passed.a;
    if (pair && passed.b?.id === pair.b) seed[pair.b] = passed.b;
    return seed;
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!pair) return;
    const missing = [pair.a, pair.b].filter((id) => !items[id]);
    if (missing.length === 0) return;
    let cancelled = false;
    Promise.all(missing.map((id) => getArtifact(id)))
      .then((views) => {
        if (cancelled) return;
        setItems((current) => {
          const next = { ...current };
          missing.forEach((id, i) => {
            next[id] = { ...views[i], kind: "OUTPUT", attrs: {}, created_at: "" } as ArtifactListItem;
          });
          return next;
        });
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "failed to load these files");
      });
    return () => {
      cancelled = true;
    };
  }, [pair, items]);

  const left = pair ? items[pair.a] : undefined;
  const right = pair ? items[pair.b] : undefined;
  const srcLeft = storeSrc(left);
  const srcRight = storeSrc(right);

  const leftRef = useRef<HTMLVideoElement>(null);
  const rightRef = useRef<HTMLVideoElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const [mode, setMode] = useState<CompareMode>("split");
  const [divider, setDivider] = useState(DEFAULT_DIVIDER_PCT);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // The seek bar's range: what the element reports once it has metadata,
  // falling back to the probe's duration for the moment before that.
  const probedDuration = left?.media?.duration_s ?? right?.media?.duration_s;
  const maxTime = duration > 0 ? duration : probedDuration && probedDuration > 0 ? probedDuration : 0;
  const playable = !!srcLeft && !!srcRight && left?.id !== right?.id;

  const play = useCallback(() => {
    const l = leftRef.current;
    const r = rightRef.current;
    if (!l || !r) return;
    const correction = driftCorrection(l.currentTime, r.currentTime);
    if (correction !== undefined) r.currentTime = correction;
    void l.play().catch(() => undefined);
    void r.play().catch(() => undefined);
  }, []);

  const pause = useCallback(() => {
    leftRef.current?.pause();
    rightRef.current?.pause();
  }, []);

  const seek = useCallback((seconds: number) => {
    if (leftRef.current) leftRef.current.currentTime = seconds;
    if (rightRef.current) rightRef.current.currentTime = seconds;
    setTime(seconds);
  }, []);

  const step = useCallback(
    (direction: 1 | -1) => {
      const l = leftRef.current;
      if (!l) return;
      l.pause();
      rightRef.current?.pause();
      seek(steppedTime(l.currentTime, direction, left?.media?.fps, maxTime || undefined));
    },
    [left?.media?.fps, maxTime, seek],
  );

  function onMasterTime() {
    const l = leftRef.current;
    const r = rightRef.current;
    if (!l || !r) return;
    setTime(l.currentTime);
    const correction = driftCorrection(l.currentTime, r.currentTime);
    if (correction !== undefined) r.currentTime = correction;
  }

  function dividerFromPointer(event: React.PointerEvent<HTMLDivElement>) {
    const rect = stageRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return;
    setDivider(clampDivider(((event.clientX - rect.left) / rect.width) * 100));
  }

  function swap() {
    if (!pair) return;
    setParams(compareSearchParams({ a: pair.b, b: pair.a }), { replace: true });
  }

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null;
      // The divider handle and the seek bar own the arrow keys while they
      // hold focus; the page shortcuts must not fight them.
      // Esc always leaves, even from the divider or the seek bar.
      if (event.key === "Escape") {
        navigate("/library");
        return;
      }
      if (target?.closest("[data-cmp-keys]")) return;
      if (event.key === " ") {
        event.preventDefault();
        if (playing) pause();
        else play();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        step(1);
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        step(-1);
      } else if (event.key === "Escape") {
        navigate("/library");
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [playing, pause, play, step, navigate]);

  if (!pair) {
    return (
      <div className="u-page">
        <div className="u-head">
          <h1 className="u-h1">Compare</h1>
          <p className="u-lede">Two versions of the same shot, on the same frame.</p>
        </div>
        <div className="u-empty">
          <p>Pick two different files to compare.</p>
          <p className="u-note">
            Open a video in the <Link to="/library">Library</Link> and choose “Compare with…”.
          </p>
        </div>
      </div>
    );
  }

  const sides: Array<{ tag: string; side: Side; item?: ArtifactListItem }> = [
    { tag: "A", side: "a", item: left },
    { tag: "B", side: "b", item: right },
  ];

  return (
    <div className="u-page u-cmp">
      <div className="u-head">
        <h1 className="u-h1">Compare</h1>
        <p className="u-lede">
          The same shot twice, on one playhead. Left is A, right is B — drag the divider to wipe
          between them.
        </p>
        <Link className="u-btn u-btn--quiet" to="/library">
          Back to Library
        </Link>
      </div>

      {error && <p className="u-error">{error}</p>}

      {!playable ? (
        <p className="u-note">
          These files can’t be played side by side yet — one of them has no read link.
        </p>
      ) : (
        <>
          <div
            className={mode === "split" ? "u-cmp-stage" : "u-cmp-stage u-cmp-stage--side"}
            ref={stageRef}
            onPointerDown={dividerFromPointer}
            onPointerMove={(e) => {
              if (e.buttons === 1) dividerFromPointer(e);
            }}
          >
            <div className="u-cmp-pane">
              <video
                key={left?.id}
                ref={leftRef}
                src={srcLeft}
                muted
                playsInline
                loop
                preload="auto"
                onTimeUpdate={onMasterTime}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
                aria-label={`A: ${left ? compareSideTitle(left) : ""}`}
              />
            </div>
            <div
              className={mode === "split" ? "u-cmp-pane u-cmp-pane--over" : "u-cmp-pane"}
              style={mode === "split" ? { clipPath: `inset(0 0 0 ${divider}%)` } : undefined}
            >
              <video
                key={right?.id}
                ref={rightRef}
                src={srcRight}
                muted
                playsInline
                loop
                preload="auto"
                aria-label={`B: ${right ? compareSideTitle(right) : ""}`}
              />
            </div>
            {mode === "split" && (
              <div
                className="u-cmp-divider"
                style={{ left: `${divider}%` }}
                role="slider"
                tabIndex={0}
                data-cmp-keys
                aria-label="Comparison divider"
                aria-valuemin={MIN_DIVIDER_PCT}
                aria-valuemax={MAX_DIVIDER_PCT}
                aria-valuenow={Math.round(divider)}
                onKeyDown={(e) => {
                  if (e.key === "ArrowLeft") {
                    e.preventDefault();
                    setDivider((d) => clampDivider(d - 2));
                  } else if (e.key === "ArrowRight") {
                    e.preventDefault();
                    setDivider((d) => clampDivider(d + 2));
                  }
                }}
              >
                <span className="u-cmp-divider__grip" aria-hidden="true" />
              </div>
            )}
          </div>

          <div className="u-cmp-controls">
            <button className="u-btn" onClick={() => (playing ? pause() : play())}>
              {playing ? "Pause" : "Play"}
            </button>
            <button className="u-btn u-btn--quiet" onClick={() => step(-1)}>
              ◀ frame
            </button>
            <button className="u-btn u-btn--quiet" onClick={() => step(1)}>
              frame ▶
            </button>
            <input
              className="u-cmp-seek"
              type="range"
              min={0}
              max={maxTime || 0}
              step="any"
              value={Math.min(time, maxTime || 0)}
              onChange={(e) => seek(Number(e.target.value))}
              data-cmp-keys
              aria-label="Playhead"
              disabled={!maxTime}
            />
            <span className="u-cmp-clock">
              {formatDuration(time)} / {maxTime ? formatDuration(maxTime) : "—"}
            </span>
            <span className="u-cmp-modes">
              <button
                className="u-btn u-btn--quiet"
                aria-pressed={mode === "split"}
                onClick={() => setMode("split")}
              >
                Split
              </button>
              <button
                className="u-btn u-btn--quiet"
                aria-pressed={mode === "side"}
                onClick={() => setMode("side")}
              >
                Side by side
              </button>
            </span>
            <button className="u-btn u-btn--quiet" onClick={swap}>
              Swap A/B
            </button>
          </div>
        </>
      )}

      <div className="u-cmp-sides">
        {sides.map(({ tag, item }) => (
          <section className="u-cmp-side" key={tag}>
            <h2 className="u-h2">
              <span className="u-cmp-side__tag">{tag}</span>
              {item ? compareSideTitle(item) : "…"}
            </h2>
            {item && compareSideLine(item) && <p className="u-note">{compareSideLine(item)}</p>}
            {item?.label && <p className="u-cmp-side__label">{item.label}</p>}
            <dl className="u-drawer__meta">
              {(item ? compareSideRows(item) : []).map((row) => (
                <Fragment key={row.label}>
                  <dt>{row.label}</dt>
                  <dd>{row.value}</dd>
                </Fragment>
              ))}
            </dl>
          </section>
        ))}
      </div>

      <p className="u-note">
        Keyboard: space plays and pauses, ← and → step one frame, Esc returns to the Library.
        {" "}
        <Link to={compareHref(pair)}>Link to this comparison</Link>.
      </p>
    </div>
  );
}
